import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, Stack } from '@mui/material';
import { useFormik } from 'formik';
import SelectInput from '../components/Select';
import TextInput from '../components/TextInput';
import { FIELD_SIZE, Section, StickyActions } from '../components/Section';
import {
  DisabilityOptions,
  extraDetailsInitialValue,
  ExtraDetailsValidationSchema
} from './Schema';
import { FoodDetails } from './Schema';
import {
  useCurrentUserDetailsQuery,
  useGetExtraDetailQuery,
  useUpdateExtraDetailMutation
} from 'redux/reducers/user/dashboard/tms-user';
import { toast } from 'react-toastify';

function ExtraDetails({
  activeStep,
  setActiveStep
}: {
  activeStep: number;
  setActiveStep: (step: number) => void;
}) {
  const { data: CurrentUser, isLoading: isCurrentUserLoading } = useCurrentUserDetailsQuery();
  const TraineeId = CurrentUser?.id;

  const {
    data: extraDetail,
    isLoading: isExtraDetailLoading,
    isSuccess
  } = useGetExtraDetailQuery(TraineeId ?? '', (data) => {
    formik.resetForm({
      values: {
        ...extraDetailsInitialValue,
        attendance_pin: data?.data?.attendance_pin_number ?? '',

        extra_fields: {
          food: data?.data?.extra_fields?.food ?? '',
          local_contact_person: {
            name: data?.data?.extra_fields?.local_contact_person?.name ?? '',
            relationship: data?.data?.extra_fields?.local_contact_person?.relationship ?? '',
            contact_number: data?.data?.extra_fields?.local_contact_person?.contact_number ?? ''
          },
          health_issue: data?.data?.extra_fields?.health_issue ?? '',
          special_needs: data?.data?.extra_fields?.special_needs ?? ''
        },
        is_backward_area: data?.data?.is_backward_area ?? false,
        is_disable_person: data?.data?.is_disable_person ?? false,
        blood_group: data?.data?.blood_group ?? '',
        disability: data?.data?.disability
      }
    });
  });

  const { mutate: updateExtraDetail, isLoading: isUpdating } = useUpdateExtraDetailMutation(
    TraineeId ?? ''
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: ExtraDetailsValidationSchema,
    initialValues: extraDetailsInitialValue,
    onSubmit: (values) => {
      const { disability, ...sendValue } = values;
      try {
        if (!values.is_disable_person) {
          updateExtraDetail(sendValue, {
            onSuccess: () => {
              toast.success('Updated Successfully');
              setActiveStep(++activeStep);
            },
            onError: () => {
              console.log('Could not save the data.');
            }
          });
        } else {
          updateExtraDetail(values, {
            onSuccess: () => {
              toast.success('Updated Successfully');
              setActiveStep(++activeStep);
            },
            onError: () => {
              console.log('Could not save the data.');
            }
          });
        }
      } catch (err) {
        console.log(err, 'lll');
      }
    }
  });

  const isSaving = isUpdating || formik.isSubmitting;

  if (isCurrentUserLoading || isExtraDetailLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Section title="Attendance & Meals" description="Used during training sessions.">
        <TextInput
          {...FIELD_SIZE}
          label="TMS Code"
          name="attendance_pin"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.attendance_pin}
          onBlur={formik.handleBlur}
          formik={formik}
          required
          readOnly
        />
        <SelectInput
          {...FIELD_SIZE}
          label="Food Details"
          name="extra_fields.food"
          options={FoodDetails || []}
          value={formik.values.extra_fields.food}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          required
        />
      </Section>

      <Section
        title="Local Contact Person"
        description="Someone we can reach if you cannot be contacted."
      >
        <TextInput
          {...FIELD_SIZE}
          label="Local Contact Person"
          name="extra_fields.local_contact_person.name"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.extra_fields.local_contact_person.name}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="Relation with Contact Person"
          name="extra_fields.local_contact_person.relationship"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.extra_fields.local_contact_person.relationship}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="Contact Person Phone No."
          name="extra_fields.local_contact_person.contact_number"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.extra_fields.local_contact_person.contact_number}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
      </Section>

      <Section
        title="Health & Accessibility"
        description="Helps us make the necessary arrangements for you."
      >
        <TextInput
          {...FIELD_SIZE}
          label="Health Issues"
          name="extra_fields.health_issue"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.extra_fields.health_issue}
          onBlur={formik.handleBlur}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Special Needs"
          name="extra_fields.special_needs"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.extra_fields.special_needs}
          onBlur={formik.handleBlur}
          formik={formik}
        />
        <TextInput
          {...FIELD_SIZE}
          label="Blood Group"
          name="blood_group"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.blood_group}
          onBlur={formik.handleBlur}
          formik={formik}
        />

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="is_disable_person"
                checked={formik.values.is_disable_person === true}
                onChange={() => {
                  formik.handleChange({
                    target: {
                      name: 'is_disable_person',
                      value: formik.values.is_disable_person === true ? false : true
                    }
                  });
                }}
              />
            }
            label="Is disabled?"
            sx={{ mb: 1 }}
          />
        </Grid>

        {formik.values.is_disable_person && (
          <TextInput
            {...FIELD_SIZE}
            label="Disability"
            name="disability"
            type="text"
            onChange={formik.handleChange}
            value={formik.values.disability}
            onBlur={formik.handleBlur}
            formik={formik}
          />
        )}
        {/* <SelectInput
          label="Disability"
          name="disability"
          options={DisabilityOptions || []}
          value={formik.values.disability}
          onChange={(e) => {
            formik.handleChange(e);
          }}
          required
        /> */}
      </Section>

      <StickyActions>
        {activeStep > 1 && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setActiveStep(--activeStep)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Previous Step
          </Button>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : undefined}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
        >
          {isSaving ? 'Saving…' : 'Save & Continue'}
        </Button>
      </StickyActions>
    </form>
  );
}

export default ExtraDetails;
